import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "../lib/api";
import { MODELS, MODEL_KEYS, ModelChip, ModelBadge } from "../components/ModelBadge";
import Markdown from "../components/Markdown";
import { Send, GitCompare, Globe } from "lucide-react";
import ModelSettings from "../components/ModelSettings";
import { useModelSettings } from "../lib/settings";

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const convId = params.get("c");
  const [selected, setSelected] = useState(["gpt-4o-mini", "claude-haiku-4-5", "gemini-2-5-flash-lite", "deepseek-chat"]);
  const [input, setInput] = useState("");
  // turns: [{ user, partials: {model: text}, done: {model: bool} }]
  const [turns, setTurns] = useState([]);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(convId || null);
  const [webSearch, setWebSearch] = useState(false);
  const { settings } = useModelSettings();
  const abortRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (convId) {
      (async () => {
        try {
          const { data } = await api.get(`/conversations/${convId}`);
          const msgs = data.messages || [];
          const ts = [];
          let current = null;
          for (const m of msgs) {
            if (m.role === "user") {
              if (current) ts.push(current);
              current = { user: m.content, partials: {}, done: {} };
            } else if (current) {
              current.partials[m.model] = m.content;
              current.done[m.model] = true;
            }
          }
          if (current) ts.push(current);
          setTurns(ts);
          if (data.conversation?.models) setSelected(data.conversation.models);
          setConversationId(convId);
        } catch {}
      })();
    } else {
      setTurns([]);
      setConversationId(null);
    }
  }, [convId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns]);

  const toggle = (k) => {
    setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  };

  const send = async () => {
    if (!input.trim() || sending || selected.length === 0) return;
    const prompt = input;
    setInput("");
    const turnIdx = turns.length;
    setTurns((t) => [...t, { user: prompt, partials: {}, done: {}, models: selected }]);
    setSending(true);

    const token = localStorage.getItem("token");
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const settingsMap = {};
    selected.forEach((m) => {
      settingsMap[m] = {
        temperature: settings[m]?.temperature,
        max_tokens: settings[m]?.max_tokens,
      };
    });

    try {
      const res = await fetch(`${API}/chat/compare/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          models: selected,
          message: prompt,
          web_search: webSearch,
          settings: settingsMap,
        }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let metaConvId = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data:")) continue;
          const payload = ln.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          let ev; try { ev = JSON.parse(payload); } catch { continue; }
          if (ev.type === "meta") {
            metaConvId = ev.conversation_id;
          } else if (ev.type === "delta" && ev.model) {
            setTurns((t) => {
              const next = [...t];
              const cur = { ...next[turnIdx] };
              cur.partials = { ...cur.partials, [ev.model]: (cur.partials[ev.model] || "") + ev.content };
              next[turnIdx] = cur;
              return next;
            });
          } else if (ev.type === "model_done" && ev.model) {
            setTurns((t) => {
              const next = [...t];
              const cur = { ...next[turnIdx] };
              cur.done = { ...cur.done, [ev.model]: true };
              next[turnIdx] = cur;
              return next;
            });
          } else if (ev.type === "error" && ev.model) {
            setTurns((t) => {
              const next = [...t];
              const cur = { ...next[turnIdx] };
              cur.partials = { ...cur.partials, [ev.model]: (cur.partials[ev.model] || "") + `\n⚠️ ${ev.error}` };
              cur.done = { ...cur.done, [ev.model]: true };
              next[turnIdx] = cur;
              return next;
            });
          }
        }
      }
      if (metaConvId && !conversationId) {
        setConversationId(metaConvId);
        queueMicrotask(() => setParams({ c: metaConvId }));
      }
      window.dispatchEvent(new Event("aimedley:conversations-changed"));
      setTimeout(() => window.dispatchEvent(new Event("aimedley:conversations-changed")), 3500);
    } catch (e) {
      setTurns((t) => {
        const next = [...t];
        const cur = { ...next[turnIdx] };
        selected.forEach((m) => {
          if (!cur.done?.[m]) {
            cur.partials = { ...cur.partials, [m]: (cur.partials[m] || "") + `\n⚠️ ${e.message}` };
            cur.done = { ...cur.done, [m]: true };
          }
        });
        next[turnIdx] = cur;
        return next;
      });
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const stop = () => { abortRef.current?.abort(); };

  const cols = selected.length;
  const gridClass =
    cols === 1 ? "grid-cols-1" :
    cols === 2 ? "grid-cols-1 lg:grid-cols-2" :
    cols === 3 ? "grid-cols-1 lg:grid-cols-3" :
    "grid-cols-1 md:grid-cols-2 xl:grid-cols-4";

  return (
    <div className="flex flex-col h-screen" data-testid="compare-page">
      <header className="px-6 py-4 border-b-2 border-slate-900 bg-white">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <GitCompare size={22} strokeWidth={2.5} />
            <div>
              <h1 className="font-heading text-2xl font-bold">Compare</h1>
              <p className="text-sm text-slate-500">Same prompt → side-by-side streaming answers.</p>
            </div>
          </div>
          <div className="text-xs uppercase font-bold text-slate-500 tracking-widest">
            {selected.length} model{selected.length === 1 ? "" : "s"} active
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center" data-testid="compare-model-selector">
          {MODEL_KEYS.map((k) => (
            <ModelChip key={k} id={k} selected={selected.includes(k)} onClick={() => toggle(k)} />
          ))}
          <ModelSettings mode="all" models={selected} />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-6" data-testid="compare-turns">
        {turns.length === 0 ? (
          <div className="text-center py-20 max-w-md mx-auto">
            <div className="flex justify-center gap-2 mb-4 float-y">
              {MODEL_KEYS.map((k) => <ModelBadge key={k} id={k} size="sm" showName={false} />)}
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Ask once. Hear from everyone.</h2>
            <p className="text-slate-500">Pick a question and watch all selected models stream their answers side-by-side.</p>
          </div>
        ) : (
          <div className="space-y-8 max-w-7xl mx-auto">
            {turns.map((t, ti) => {
              const turnModels = t.models || selected;
              return (
                <div key={ti} className="space-y-3">
                  <div className="flex justify-center">
                    <div className="brut-card px-4 py-2 max-w-2xl text-center font-semibold" data-testid={`compare-user-${ti}`}>
                      {t.user}
                    </div>
                  </div>
                  <div className={`grid ${gridClass} gap-4`}>
                    {turnModels.map((mk) => {
                      const m = MODELS[mk];
                      const text = t.partials?.[mk] || "";
                      const isDone = t.done?.[mk];
                      return (
                        <div
                          key={mk}
                          className="border-2 rounded-2xl flex flex-col min-h-[180px]"
                          style={{ borderColor: m.border, backgroundColor: "#fff" }}
                          data-testid={`compare-col-${mk}-${ti}`}
                        >
                          <div
                            className="px-4 py-2 border-b-2 sticky top-0 rounded-t-2xl flex items-center gap-2"
                            style={{ backgroundColor: m.bg, borderColor: m.border, color: m.border }}
                          >
                            <ModelBadge id={mk} size="sm" />
                            <span className="text-xs font-bold uppercase tracking-wider">{m.personality}</span>
                            {!isDone && text && (
                              <span className="ml-auto text-[10px] font-bold tracking-widest flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full dot-blink" style={{ backgroundColor: m.border }} />
                                STREAMING
                              </span>
                            )}
                          </div>
                          <div className="p-4 text-sm" style={{ backgroundColor: m.bg + "55" }}>
                            {!text && !isDone ? (
                              <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border }} />
                                <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border, animationDelay: "0.2s" }} />
                                <span className="w-2 h-2 rounded-full dot-blink" style={{ backgroundColor: m.border, animationDelay: "0.4s" }} />
                              </div>
                            ) : (
                              <Markdown text={text} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <footer className="border-t-2 border-slate-900 bg-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <button
              data-testid="compare-web-toggle"
              onClick={() => setWebSearch((w) => !w)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading font-bold border-2 transition-all ${
                webSearch
                  ? "bg-emerald-500 text-white border-emerald-700 shadow-brut-sm"
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"
              }`}
            >
              <Globe size={14} strokeWidth={2.5} />
              Web search {webSearch ? "ON" : "OFF"}
            </button>
          </div>
          <div className="flex items-end gap-3">
            <textarea
              data-testid="compare-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Ask all selected models the same question…"
              className="brut-input flex-1 resize-none max-h-32"
              style={{ minHeight: 48 }}
            />
            {sending ? (
              <button data-testid="compare-stop-btn" onClick={stop} className="brut-btn brut-btn-outline">Stop</button>
            ) : (
              <button
                data-testid="compare-send-btn"
                onClick={send}
                disabled={!input.trim() || selected.length === 0}
                className="brut-btn"
              >
                <Send size={16} strokeWidth={2.5} /> Ask all
              </button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
